import { useEffect, useState } from "react";

const STORAGE_KEY = "pageViewCounts";

export interface PageViewData {
  count: number;
  lastVisited: string;
  firstVisited: string;
}

const getViewData = (): Record<string, PageViewData> => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveViewData = (data: Record<string, PageViewData>) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

export const usePageView = (pageId: string, pageType: "place" | "restaurant") => {
  const key = `${pageType}:${pageId}`;
  const [viewCount, setViewCount] = useState<number>(0);

  useEffect(() => {
    if (!pageId) return;

    const all = getViewData();
    const now = new Date().toISOString();
    const existing = all[key];

    const updated: PageViewData = {
      count: (existing?.count ?? 0) + 1,
      lastVisited: now,
      firstVisited: existing?.firstVisited ?? now,
    };

    all[key] = updated;
    saveViewData(all);
    setViewCount(updated.count);
  }, [key, pageId]);

  return viewCount;
};

// Utility: get all view counts for admin panel
export const getAllPageViews = (): Record<string, PageViewData & { pageId: string; pageType: string }> => {
  const raw = getViewData();
  const result: Record<string, PageViewData & { pageId: string; pageType: string }> = {};
  Object.entries(raw).forEach(([key, data]) => {
    const [pageType, ...rest] = key.split(":");
    const pageId = rest.join(":");
    result[key] = { ...data, pageId, pageType };
  });
  return result;
};

// Utility: get top viewed pages
export const getTopViewedPages = (
  type: "place" | "restaurant" | "all",
  limit = 10
): Array<{ key: string; pageId: string; pageType: string; count: number; lastVisited: string }> => {
  const raw = getViewData();
  return Object.entries(raw)
    .filter(([key]) => type === "all" || key.startsWith(`${type}:`))
    .map(([key, data]) => {
      const [pageType, ...rest] = key.split(":");
      return { key, pageId: rest.join(":"), pageType, count: data.count, lastVisited: data.lastVisited };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};
